/**
 * GrassyJSON - GeoJSON Utility Library
 *
 * A comprehensive GeoJSON utility package for cleaning, merging, and processing
 * GeoJSON features with advanced geospatial operations.
 */

import * as turf from "@turf/turf";
import type {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  Geometry,
  Position,
} from "geojson";

// ============================================================================
// Types
// ============================================================================

export type ComparisonFunction = (
  featureA: Feature<Geometry, GeoJsonProperties>,
  featureB: Feature<Geometry, GeoJsonProperties>
) => boolean;

export interface GrassyJSONOptions {
  /** Default buffer distance in meters for merge operations */
  bufferDistance?: number;
  /** Default coordinate precision (decimal places) */
  precision?: number;
  /** Snap tolerance in meters for border snapping */
  snapTolerance?: number;
}

// ============================================================================
// Configuration
// ============================================================================

const DEFAULT_OPTIONS: Required<GrassyJSONOptions> = {
  bufferDistance: 200, // 200 meters (for simplified GeoJSON files)
  precision: 6, // ~0.11m precision at equator
  snapTolerance: 10, // 10 meters
};

let globalOptions: Required<GrassyJSONOptions> = { ...DEFAULT_OPTIONS };

/**
 * Configure global options for GrassyJSON operations
 */
export function configure(options: GrassyJSONOptions): void {
  globalOptions = { ...globalOptions, ...options };
}

/**
 * Reset global options to defaults
 */
export function resetConfig(): void {
  globalOptions = { ...DEFAULT_OPTIONS };
}

// ============================================================================
// Core Operations
// ============================================================================

/**
 * Merge multiple features into one (or multiple) features using a buffer-union-shrink approach.
 *
 * Algorithm:
 * 1. Buffer each feature by X meters to smooth out poor line drawing
 * 2. Take the union of all buffered areas
 * 3. Shrink the result by X meters to return to original size
 *
 * This approach helps clean up features that were drawn with slight gaps or overlaps.
 *
 * @param features - Array of features to merge
 * @param bufferDistance - Buffer distance in meters (default: 100m)
 * @param precision - Coordinate precision in decimal places (default: 6)
 * @returns Merged feature(s) as a FeatureCollection
 *
 * @example
 * ```ts
 * const merged = mergeFeatures([featureA, featureB, featureC]);
 * ```
 */
export function mergeFeatures(
  features: Feature<Geometry, GeoJsonProperties>[],
  bufferDistance: number = globalOptions.bufferDistance,
  precision: number = globalOptions.precision
): FeatureCollection<Geometry, GeoJsonProperties> {
  if (features.length === 0) {
    return turf.featureCollection([]);
  }

  if (features.length === 1) {
    return turf.featureCollection([features[0]]);
  }

  try {
    // Step 1: Buffer all features
    const buffered = features.map((feature) => {
      try {
        return turf.buffer(feature, bufferDistance, { units: "meters" });
      } catch (err) {
        console.warn("Failed to buffer feature:", err);
        return feature;
      }
    });

    // Step 2: Union all buffered features
    let union: Feature | undefined = buffered[0];
    for (let i = 1; i < buffered.length; i++) {
      if (!union) continue;
      try {
        const unionResult = turf.union(turf.featureCollection([union, buffered[i]!])) as Feature | null;
        if (unionResult) {
          union = unionResult;
        }
      } catch (err) {
        console.warn("Failed to union features:", err);
        // Continue with current union
      }
    }

    if (!union) {
      return turf.featureCollection(features);
    }

    // Step 3: Negative buffer (shrink) to return to original size
    let result: Feature | null = null;
    try {
      result = turf.buffer(union, -bufferDistance, { units: "meters" }) as Feature | null;
    } catch (err) {
      console.warn("Failed to shrink result, returning union:", err);
      result = union;
    }

    if (!result) {
      return turf.featureCollection([union]);
    }

    // Merge properties from all input features
    const mergedProperties: GeoJsonProperties = {};
    features.forEach((feature) => {
      if (feature.properties) {
        Object.entries(feature.properties).forEach(([key, value]) => {
          if (mergedProperties[key] === undefined) {
            mergedProperties[key] = value;
          } else if (Array.isArray(mergedProperties[key])) {
            // Already an array, add if not duplicate
            if (!mergedProperties[key].includes(value)) {
              mergedProperties[key].push(value);
            }
          } else if (mergedProperties[key] !== value) {
            // Different values, convert to array
            mergedProperties[key] = [mergedProperties[key], value];
          }
        });
      }
    });

    result.properties = mergedProperties;

    // Apply precision to avoid floating point issues
    const rounded = turf.truncate(result, { precision, coordinates: 2 });
    return turf.featureCollection([rounded]);
  } catch (err) {
    console.error("Failed to merge features:", err);
    // Return original features as fallback
    return turf.featureCollection(features);
  }
}

/**
 * Automatically merge features based on a comparison function.
 *
 * Uses an O(n) algorithm with spatial indexing to efficiently find matching features.
 * Features that match according to the comparison function are merged using mergeFeatures.
 *
 * @param collection - GeoJSON FeatureCollection
 * @param comparisonFunc - Function to determine if two features should be merged
 * @param bufferDistance - Buffer distance for merge operation
 * @param precision - Coordinate precision in decimal places (default: 6)
 * @returns New FeatureCollection with merged features
 *
 * @example
 * ```ts
 * // Merge features with the same country code
 * const merged = autoMerge(collection, (a, b) =>
 *   a.properties?.country === b.properties?.country
 * );
 * ```
 */
export function autoMerge(
  collection: FeatureCollection<Geometry, GeoJsonProperties>,
  comparisonFunc: ComparisonFunction,
  bufferDistance: number = globalOptions.bufferDistance,
  precision: number = globalOptions.precision
): FeatureCollection<Geometry, GeoJsonProperties> {
  if (collection.features.length === 0) {
    return collection;
  }

  // Group features by comparison function
  // Use Union-Find (Disjoint Set) for O(n) complexity
  const parent = new Map<number, number>();
  const rank = new Map<number, number>();

  // Initialize disjoint set
  for (let i = 0; i < collection.features.length; i++) {
    parent.set(i, i);
    rank.set(i, 0);
  }

  // Find with path compression
  function find(x: number): number {
    if (parent.get(x) !== x) {
      parent.set(x, find(parent.get(x)!));
    }
    return parent.get(x)!;
  }

  // Union by rank
  function union(x: number, y: number): void {
    const rootX = find(x);
    const rootY = find(y);

    if (rootX === rootY) return;

    const rankX = rank.get(rootX)!;
    const rankY = rank.get(rootY)!;

    if (rankX < rankY) {
      parent.set(rootX, rootY);
    } else if (rankX > rankY) {
      parent.set(rootY, rootX);
    } else {
      parent.set(rootY, rootX);
      rank.set(rootX, rankX + 1);
    }
  }

  // Build spatial index for efficient neighbor finding
  // Group features into grid cells based on bounding boxes
  const cellSize = 1; // 1 degree cells
  const grid = new Map<string, number[]>();

  collection.features.forEach((feature, idx) => {
    try {
      const bbox = turf.bbox(feature);
      const [minLng, minLat, maxLng, maxLat] = bbox;

      // Add feature to all cells it overlaps
      const minCellX = Math.floor(minLng / cellSize);
      const maxCellX = Math.floor(maxLng / cellSize);
      const minCellY = Math.floor(minLat / cellSize);
      const maxCellY = Math.floor(maxLat / cellSize);

      for (let x = minCellX; x <= maxCellX; x++) {
        for (let y = minCellY; y <= maxCellY; y++) {
          const cellKey = `${x},${y}`;
          if (!grid.has(cellKey)) {
            grid.set(cellKey, []);
          }
          grid.get(cellKey)!.push(idx);
        }
      }
    } catch (err) {
      console.warn("Failed to index feature:", err);
    }
  });

  // Compare features within same/adjacent cells only (O(n) on average)
  const compared = new Set<string>();

  collection.features.forEach((featureA, idxA) => {
    try {
      const bbox = turf.bbox(featureA);
      const [minLng, minLat, maxLng, maxLat] = bbox;

      const minCellX = Math.floor(minLng / cellSize);
      const maxCellX = Math.floor(maxLng / cellSize);
      const minCellY = Math.floor(minLat / cellSize);
      const maxCellY = Math.floor(maxLat / cellSize);

      // Check features in same and adjacent cells
      for (let x = minCellX - 1; x <= maxCellX + 1; x++) {
        for (let y = minCellY - 1; y <= maxCellY + 1; y++) {
          const cellKey = `${x},${y}`;
          const cellFeatures = grid.get(cellKey) || [];

          cellFeatures.forEach((idxB) => {
            if (idxA >= idxB) return; // Only compare each pair once

            const pairKey = `${idxA},${idxB}`;
            if (compared.has(pairKey)) return;
            compared.add(pairKey);

            const featureB = collection.features[idxB];

            try {
              if (comparisonFunc(featureA, featureB)) {
                union(idxA, idxB);
              }
            } catch (err) {
              console.warn("Comparison function error:", err);
            }
          });
        }
      }
    } catch (err) {
      console.warn("Failed to process feature:", err);
    }
  });

  // Group features by root parent
  const groups = new Map<number, Feature<Geometry, GeoJsonProperties>[]>();
  collection.features.forEach((feature, idx) => {
    const root = find(idx);
    if (!groups.has(root)) {
      groups.set(root, []);
    }
    groups.get(root)!.push(feature);
  });

  // Merge each group
  const mergedFeatures: Feature<Geometry, GeoJsonProperties>[] = [];
  groups.forEach((group) => {
    if (group.length === 1) {
      mergedFeatures.push(group[0]);
    } else {
      const merged = mergeFeatures(group, bufferDistance, precision);
      mergedFeatures.push(...merged.features);
    }
  });

  // Apply precision to final result
  const result = turf.featureCollection(mergedFeatures);
  return setAccuracy(result, precision);
}

/**
 * Snap borders together to eliminate gaps and overlaps.
 *
 * This function finds vertices that are close to edges of other features
 * and snaps them together, creating clean shared borders.
 *
 * Algorithm processes features sequentially, snapping each feature's vertices
 * to the edges of already-processed features. This avoids double-compensation
 * where both sides get moved.
 *
 * @param collection - GeoJSON FeatureCollection
 * @param tolerance - Snap tolerance in meters (default: 10m)
 * @param precision - Coordinate precision in decimal places (default: 6)
 * @returns FeatureCollection with snapped borders
 *
 * @example
 * ```ts
 * const snapped = snapBorder(collection, 50); // 50m tolerance
 * ```
 */
export function snapBorder(
  collection: FeatureCollection<Geometry, GeoJsonProperties>,
  tolerance: number = globalOptions.snapTolerance,
  precision: number = globalOptions.precision
): FeatureCollection<Geometry, GeoJsonProperties> {
  if (collection.features.length <= 1) {
    return collection;
  }

  // Convert tolerance from meters to degrees (approximate)
  const toleranceDegrees = tolerance / 111000; // ~111km per degree at equator

  // Process features sequentially to avoid double-compensation
  const processedFeatures: Feature<Geometry, GeoJsonProperties>[] = [];

  for (let i = 0; i < collection.features.length; i++) {
    const currentFeature = collection.features[i];

    // Extract segments from all previously processed features
    const existingSegments: { start: Position; end: Position }[] = [];

    processedFeatures.forEach((processedFeature) => {
      try {
        const coords = turf.getCoords(processedFeature as any);
        extractSegmentsSimple(coords, existingSegments);
      } catch (err) {
        console.warn("Failed to extract segments:", err);
      }
    });

    // Snap current feature's vertices to existing segments
    try {
      const coords = turf.getCoords(currentFeature as any);
      const snappedCoords = snapCoordinatesToSegments(coords, existingSegments, toleranceDegrees);

      // Create new feature with snapped coordinates
      const newFeature = { ...currentFeature };
      if (currentFeature.geometry.type === "Polygon") {
        newFeature.geometry = {
          type: "Polygon",
          coordinates: snappedCoords as Position[][],
        };
      } else if (currentFeature.geometry.type === "MultiPolygon") {
        newFeature.geometry = {
          type: "MultiPolygon",
          coordinates: snappedCoords as Position[][][],
        };
      }

      // Apply precision
      const rounded = turf.truncate(newFeature, { precision, coordinates: 2 });
      processedFeatures.push(rounded);
    } catch (err) {
      console.warn("Failed to snap feature:", err);
      processedFeatures.push(currentFeature);
    }
  }

  return turf.featureCollection(processedFeatures);
}

// Helper: Extract line segments from coordinates (simple version)
function extractSegmentsSimple(
  coords: any,
  segments: { start: Position; end: Position }[]
): void {
  if (!Array.isArray(coords)) return;

  if (typeof coords[0] === "number") {
    // This is a single position, skip
    return;
  }

  if (typeof coords[0][0] === "number") {
    // This is an array of positions (a ring)
    for (let i = 0; i < coords.length - 1; i++) {
      segments.push({
        start: coords[i],
        end: coords[i + 1],
      });
    }
  } else {
    // Nested arrays (multiple rings or polygons)
    coords.forEach((subCoords) => extractSegmentsSimple(subCoords, segments));
  }
}

// Helper: Snap coordinates to nearby segments
function snapCoordinatesToSegments(
  coords: any,
  segments: { start: Position; end: Position }[],
  tolerance: number
): any {
  if (!Array.isArray(coords)) return coords;

  if (typeof coords[0] === "number") {
    // This is a single position - snap it
    return snapPositionToSegments(coords as Position, segments, tolerance);
  }

  // Recursively snap nested arrays
  return coords.map((subCoords) =>
    snapCoordinatesToSegments(subCoords, segments, tolerance)
  );
}

// Helper: Snap a single position to nearby segment
function snapPositionToSegments(
  pos: Position,
  segments: { start: Position; end: Position }[],
  tolerance: number
): Position {
  if (segments.length === 0) {
    return pos; // First feature, nothing to snap to
  }

  let closestDist = tolerance;
  let closestPoint: Position | null = null;

  // Check distance to all segments
  segments.forEach((seg) => {
    const point = turf.point(pos);
    const line = turf.lineString([seg.start, seg.end]);

    try {
      const nearest = turf.nearestPointOnLine(line, point);
      const dist = turf.distance(point, nearest, { units: "degrees" });

      if (dist < closestDist) {
        closestDist = dist;
        closestPoint = nearest.geometry.coordinates;
      }
    } catch (err) {
      // Skip problematic segments
    }
  });

  return closestPoint || pos;
}

/**
 * Flatten overlapping features into non-overlapping layers.
 *
 * Takes features that may overlap and creates a new set of features where
 * each feature represents a unique combination of overlapping inputs.
 * Properties from overlapping features are merged (converted to arrays for multi-values).
 *
 * @param collection - GeoJSON FeatureCollection
 * @param precision - Coordinate precision in decimal places (default: 6)
 * @returns FeatureCollection with flattened, non-overlapping features
 *
 * @example
 * ```ts
 * // Input: Two overlapping phone code regions
 * // Output: Three regions (A only, B only, A+B intersection)
 * const flattened = flattenLayers(collection);
 * ```
 */
export function flattenLayers(
  collection: FeatureCollection<Geometry, GeoJsonProperties>,
  precision: number = globalOptions.precision
): FeatureCollection<Geometry, GeoJsonProperties> {
  if (collection.features.length <= 1) {
    return collection;
  }

  // Start with first feature
  let layers: Feature<Geometry, GeoJsonProperties>[] = [collection.features[0]];

  // Process each subsequent feature
  for (let i = 1; i < collection.features.length; i++) {
    const newFeature = collection.features[i];
    const newLayers: Feature<Geometry, GeoJsonProperties>[] = [];
    let remainingNewFeature: Feature | null = newFeature;

    layers.forEach((existingLayer) => {
      try {
        if (!remainingNewFeature) return;

        // Calculate intersection and differences
        const intersection = turf.intersect(
          turf.featureCollection([existingLayer, remainingNewFeature])
        ) as Feature | null;
        const existingOnly = turf.difference(
          turf.featureCollection([existingLayer, remainingNewFeature!])
        ) as Feature | null;

        // Update remaining part of new feature (subtract what we've processed)
        if (intersection) {
          remainingNewFeature = turf.difference(
            turf.featureCollection([remainingNewFeature, existingLayer])
          ) as Feature | null;
        }

        // Add non-overlapping part of existing layer
        if (existingOnly) {
          existingOnly.properties = { ...existingLayer.properties };
          newLayers.push(existingOnly);
        }

        // Add intersection with merged properties
        if (intersection) {
          intersection.properties = mergeProperties(
            existingLayer.properties,
            newFeature?.properties || null
          );
          newLayers.push(intersection);
        }
      } catch (err) {
        console.warn("Failed to process layer intersection:", err);
        // Keep existing layer on error
        newLayers.push(existingLayer);
      }
    });

    // Add any remaining part of new feature (doesn't overlap with any existing layer)
    if (remainingNewFeature && newFeature) {
      remainingNewFeature.properties = { ...newFeature.properties };
      newLayers.push(remainingNewFeature);
    }

    layers = newLayers;
  }

  // Apply precision to final result
  const result = turf.featureCollection(layers);
  return setAccuracy(result, precision);
}

// Helper: Merge properties from two features
function mergeProperties(
  propsA: GeoJsonProperties,
  propsB: GeoJsonProperties
): GeoJsonProperties {
  const merged: GeoJsonProperties = { ...propsA };

  if (!propsB) return merged;

  Object.entries(propsB).forEach(([key, valueB]) => {
    if (merged[key] === undefined) {
      // Key only in B
      merged[key] = valueB;
    } else if (merged[key] === valueB) {
      // Same value, keep as-is
      return;
    } else {
      // Different values - convert to array (Set-like, no duplicates)
      const existing = merged[key];

      if (Array.isArray(existing)) {
        // Already an array
        if (!existing.includes(valueB)) {
          merged[key] = [...existing, valueB];
        }
      } else {
        // Convert to array
        merged[key] = [existing, valueB];
      }
    }
  });

  return merged;
}

/**
 * Set coordinate precision for all features in a collection.
 *
 * Rounds all coordinates to the specified number of decimal places.
 * This reduces file size and can help eliminate micro-gaps from floating point errors.
 *
 * Precision guide:
 * - 6 decimals: ~0.11m precision (good default)
 * - 5 decimals: ~1.1m precision
 * - 4 decimals: ~11m precision
 * - 3 decimals: ~110m precision
 *
 * @param collection - GeoJSON FeatureCollection
 * @param precision - Number of decimal places (default: 6)
 * @returns FeatureCollection with rounded coordinates
 *
 * @example
 * ```ts
 * // Round to 4 decimal places (~11m precision)
 * const rounded = setAccuracy(collection, 4);
 * ```
 */
export function setAccuracy(
  collection: FeatureCollection<Geometry, GeoJsonProperties>,
  precision: number = globalOptions.precision
): FeatureCollection<Geometry, GeoJsonProperties> {
  const roundedFeatures = collection.features.map((feature) => {
    try {
      const rounded = turf.truncate(feature, { precision, coordinates: 2 });
      return rounded;
    } catch (err) {
      console.warn("Failed to round feature coordinates:", err);
      return feature;
    }
  });

  return turf.featureCollection(roundedFeatures);
}

// ============================================================================
// Convenience Exports
// ============================================================================

/**
 * GrassyJSON namespace with all utilities
 */
export const GrassyJSON = {
  // Configuration
  configure,
  resetConfig,

  // Core operations
  mergeFeatures,
  autoMerge,
  snapBorder,
  flattenLayers,
  setAccuracy,
};

// Default export
export default GrassyJSON;
